import React, { useState } from "react";

import { Document } from "../objects/Document.interface";
import { Entity } from "../objects/Entity.interface";
import { Project } from "../objects/Project.interface";
import { Annotation } from '../objects/Annotation.interface';

import { AnnotationService } from "../services/Annotations.service";
import { DocumentService } from "../services/Documents.service";
import { EntitiesService } from "../services/Entities.service";
import { ProjectsService } from "../services/Projects.service";

import { FileParser } from "../tools/FileParser";
import { ParseEntitiesJSON, ReadFileToText } from "../tools/FileReader";
import { ImportData } from "../tools/ImportData";

import Button from "@mui/material/Button";


export type ProjectImportProps = {
    backProjectsScreen: () => void,
    openDashBoard: (project:Project) => void
}

export default function ProjectImport(props:ProjectImportProps){
    const projectService = new ProjectsService();
    const documentService =  new DocumentService();
    const entityService = new EntitiesService();
    const annotationService = new AnnotationService();
    const parseFileService: FileParser = new FileParser();
    const importService: ImportData = new ImportData();

    const [projectId, setProjectId] = useState(-1);
    const [entityMessage, setEntityMessage] = useState('');
    const [documentsMessage, setDocumentMessage] = useState('');
    const [annotationsMessage, setAnnotationsMessage] = useState('');
    const [entityStatus, setEntityStatus] = useState(false);
    const [documentsStatus, setDocumentStatus] = useState(false);
    const [annotationsStatus, setAnnotationsStatus] = useState(false);

    /**
     * Inicializador de carga de un nuevo documento de texto (corpus) para agregar a la lista
     * @param e Evento de selecccion de documento de texto (corpus)
     */
     const handleDocumentFileChange = (e:any) => {
        // Check if user has entered the file
        if (e.target.files.length) {
            var files = e.target.files;
            var resultAll = documentService.getAll();
            resultAll.then(res=> {
                var documentAll = res? (res as Document[]): ([] as Document[]); 
                parseFileService.parseFiles(files,projectId, documentAll);
                setDocumentMessage(files.length + ' documents have been imported into the project.');
                setDocumentStatus(true);
               
                
            });
           
        }
    }

    /**
     * Inicializador de carga de un nuevo documento json (annotaciones) para agregar al projecto
     * @param e Evento de selecccion de documento json (anotaciones)
     */
     const handleAnnotationFileChange = async (e:any) => {
        // Check if user has entered the file
        if (e.target.files.length) {
            var files = e.target.files;
            
            var resultAll = documentService.getList(projectId);
            await resultAll.then(async res=> {
                var documents = res? (res as Document[]): ([] as Document[]); 

                var entityResult = entityService.getList(projectId);
                await entityResult.then(async entities =>{
                    var entityList = entities? (entities as Entity[]): ([] as Entity[]); 
                    await parseFileService.parseAnnotationFiles(files, documents, entityList);
                    setAnnotationsMessage('Annotations have been imported into the project.');
                    setAnnotationsStatus(true);
                });
            });
           
        }
    }

    const handleEntitiesFileChange = async (e:any) => {
        // Check if user has entered the file
        if (e.target.files.length) {
            const inputFile = e.target.files[0];
            var fileContent = await ReadFileToText(inputFile);
            let jsonData = JSON.parse(fileContent);
            var resultAdd = projectService.add({name: jsonData.name, description: jsonData.description});
            await resultAdd.then(async id => {
                if(id >= 0){
                    setProjectId(id);
                    var listTmp:Entity[] = ParseEntitiesJSON(fileContent,id);
                    await importService.SaveEntities(listTmp,id);
                    setEntityMessage('Entities have been imported into the project.');
                    setEntityStatus(true);
                }
            });
        }
    }

    const handleOpenDashboard =  () => {
        var resultAdd = projectService.getProjectById(projectId);
        resultAdd.then( projects => {
            if(projects.length > 0){
                props.openDashBoard(projects[0]);
            }
        });
    }

    const disabledButtons = ():boolean => {
        return (projectId < 0)?true:false;
    }

    const updateDataImported = () =>{
        if(projectId > -1){
            var documentList:Document[] = [];

            var entityResult = entityService.getList(projectId);
            entityResult.then(entities =>{
                var entityList = entities? (entities as Entity[]): ([] as Entity[]); 
                if(entityList.length > 0){
                    setEntityMessage(entityList.length + ' entities have been imported into the project.');
                    setEntityStatus(true);
                }
            });

            var resultAll = documentService.getList(projectId);
            resultAll.then(documents=> {
                documentList = documents? (documents as Document[]): ([] as Document[]); 
               
                if(documentList.length > 0){
                    var documentIds = documentList.map(document => document.id);
                    setDocumentMessage(documentList.length + ' documents have been imported into the project.');
                    setDocumentStatus(true);

                    var annotationsResult = annotationService.getAllAnnotations();
                    annotationsResult.then(annotations =>{
                        var annotationList = annotations? (annotations as Annotation[]): ([] as Annotation[]);
        
                        var annotationFiltered = annotationList.filter(annotation => documentIds.includes(annotation.documentId) == true);

                        setAnnotationsMessage(annotationFiltered.length + ' annotations have been imported into the project.');
                        setAnnotationsStatus(true);
                    });
                }
            });

            
        }
    }


    return (
    <div className="area-tool-container">
        <div className="area-entities-header">
            <div className="area-entities-header-content">
                <div  className="entity-selected-data-name"><span className="entity-selected-back" onClick={props.backProjectsScreen}>Projects </span><span> / Details</span></div>     
            </div>
                <div id="area-corpus-separator" className="separator"></div>
        </div>
        <div className="area-entity-details-info">

            {
                entityStatus == false &&
                <div className="import-step-container">
                    <h1 className="import-step-title">Step 1:</h1>
                    <h1 className="import-step-description">Upload the file named annotations-legend.json, which contains the list of entities, fields, and project details.</h1>
                    <input type="file" onChange={handleEntitiesFileChange} name="file-1" id="entitiesFile" accept=".json" className="inputfile-document inputfile-document-1"  multiple/>
                    <label htmlFor="entitiesFile" >
                        <span className="area-documents-add">Upload annotations-legend.json file</span>
                    </label>
                </div>
            }


            {
                entityStatus == true && <div className="import-step-container">
                    <h1 className="import-step-title">Step 1:</h1>
                    <h1 className="import-step-description">{entityMessage}</h1>
                </div>
            }
            
            {
                documentsStatus == false &&
                <div className="import-step-container">
                    <h1 className="import-step-title">Step 2:</h1>
                    <h1 className="import-step-description">Upload the files with the .txt extension located in the ANN_documents folder.</h1>
                    <input type="file" disabled={disabledButtons()} onChange={handleDocumentFileChange} name="file-1" id="documentFile" accept=".txt" className={(disabledButtons()== true)?'inputfile-document inputfile-document-1-disabled':'inputfile-document inputfile-document-1'} multiple/>
                    <label htmlFor="documentFile" >
                        <span className={(disabledButtons()== true)?'':'area-documents-add'}>Upload .txt files</span>
                    </label>
                </div>
            }
            {
                documentsStatus == true &&
                <div className="import-step-container">
                    <h1 className="import-step-title">Step 2:</h1>
                    <h1 className="import-step-description">{documentsMessage}</h1>
                </div>
            }
            
            {
                annotationsStatus == false &&
                <div className="import-step-container">
                    <h1 className="import-step-title">Step 3:</h1>
                    <h1 className="import-step-description">Upload the files with the .json extension located in the ANN_documents folder.</h1>
                    <input type="file" disabled={disabledButtons()} onChange={handleAnnotationFileChange} name="file-1" id="annotationsFile" accept=".json" className={(disabledButtons()== true)?'inputfile-document inputfile-document-1-disabled':'inputfile-document inputfile-document-1'}  multiple/>
                    <label htmlFor="annotationsFile" >
                        <span className={(disabledButtons()== true)?'':'area-documents-add'}>Upload .json files</span>
                    </label>
                </div>
            }
            {
                annotationsStatus == true &&
                <div className="import-step-container">
                    <h1 className="import-step-title">Step 3:</h1>
                    <h1 className="import-step-description">{annotationsMessage}</h1>
                </div>
            }
            <Button  variant="contained" color={(annotationsStatus)?'success':'inherit'} onClick={handleOpenDashboard}>
                open dashboard
            </Button>
        </div>
    </div>)
}

